import React, { Suspense } from 'react';
// lazy() that reloads once when a page file vanished after a deploy
import lazy from './utils/lazyWithReload';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { canBuyInApp, isNativeApp } from './utils/native';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './components/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ConfirmDialogProvider } from './context/ConfirmDialogContext';
import { ThemeProvider } from './context/ThemeContext';
import MaintenanceAlert from './components/MaintenanceAlert';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SignupPage from './pages/SignupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ModulesInfoPage from './pages/ModulesInfoPage';
import PartnerPage from './pages/PartnerPage';
import CookieConsent from './components/CookieConsent';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import DashboardPage from './pages/DashboardPage';
import SuperuserPanel from './pages/SuperuserPanel';
import UsersPage from './pages/UsersPage';
import CompaniesPage from './pages/CompaniesPage';
import CompanyLicensesPage from './pages/CompanyLicensesPage';
import SupplierLoginPage from './pages/SupplierLoginPage';
import LandingPage from './pages/LandingPage';
import ModuleDetailPage from './pages/landing/ModuleDetailPage';
import SystemHealthPage from './pages/SystemHealthPage';
import './App.css';
import { setToastContextWithQueue } from './utils/axiosConfig';
import { useToast } from './context/ToastContext';
import DeploymentNotification from './components/DeploymentNotification';

import './i18n'; // Initialize i18n
const ServiceModule = lazy(() => import('./pages/modules/service/ServiceModule'));
const PhonePage = lazy(() => import('./pages/PhonePage'));
const CustomersPage = lazy(() => import('./pages/modules/service/CustomersPage'));
const ServiceTicketsPage = lazy(() => import('./pages/modules/service/ServiceTicketsPage'));
const ServiceTicketDetailPage = lazy(() => import('./pages/modules/service/ServiceTicketDetailPage'));
const ServiceTicketFormPage = lazy(() => import('./pages/modules/service/ServiceTicketFormPage'));
const ServiceReportsPage = lazy(() => import('./pages/modules/service/ServiceReportsPage'));
const TechnicianCalendarPage = lazy(() => import('./pages/modules/service/TechnicianCalendarPage'));
const CustomerFeedbackPage = lazy(() => import('./pages/modules/service/CustomerFeedbackPage'));
const SparePartsPage = lazy(() => import('./pages/modules/service/SparePartsPage'));
const AppointmentsPage = lazy(() => import('./pages/modules/service/AppointmentsPage'));
const ProductCategoriesPage = lazy(() => import('./pages/modules/service/ProductCategoriesPage'));
const ProductsPage = lazy(() => import('./pages/modules/service/ProductsPage'));
const ServiceContractsPage = lazy(() => import('./pages/modules/service/ServiceContractsPage'));
const ServiceWorkOrdersPage = lazy(() => import('./pages/modules/service/ServiceWorkOrdersPage'));
const ServiceMaintenancePlansPage = lazy(() => import('./pages/modules/service/ServiceMaintenancePlansPage'));
const ServiceHistoryPage = lazy(() => import('./pages/modules/service/ServiceHistoryPage'));
const WarrantyClaimsPage = lazy(() => import('./pages/modules/service/WarrantyClaimsPage'));
const HRModule = lazy(() => import('./pages/modules/hr/HRModule'));
const DepartmentsPage = lazy(() => import('./pages/modules/hr/DepartmentsPage'));
const PositionsPage = lazy(() => import('./pages/modules/hr/PositionsPage'));
const EmployeesPage = lazy(() => import('./pages/modules/hr/EmployeesPage'));
const EmployeeDetailPage = lazy(() => import('./pages/modules/hr/EmployeeDetailPage'));
const EmployeeFormPage = lazy(() => import('./pages/modules/hr/EmployeeFormPage'));
const LeaveTypesPage = lazy(() => import('./pages/modules/hr/LeaveTypesPage'));
const LeaveRequestsPage = lazy(() => import('./pages/modules/hr/LeaveRequestsPage'));
const MyLeavesPage = lazy(() => import('./pages/modules/hr/MyLeavesPage'));
const MyPayslipsPage = lazy(() => import('./pages/modules/hr/MyPayslipsPage'));
const AttendancePage = lazy(() => import('./pages/modules/hr/AttendancePage'));
const LeaveApprovalsPage = lazy(() => import('./pages/modules/hr/LeaveApprovalsPage'));
const PayrollPage = lazy(() => import('./pages/modules/hr/PayrollPage'));
const HRDashboard = lazy(() => import('./pages/modules/hr/HRDashboard'));
const OrganizationChartPage = lazy(() => import('./pages/modules/hr/OrganizationChartPage'));
// Payment Pages
const LicensePlansPage = lazy(() => import('./pages/payment/LicensePlansPage'));
const PaymentPage = lazy(() => import('./pages/payment/PaymentPage'));
const PaymentSuccessPage = lazy(() => import('./pages/payment/PaymentSuccessPage'));
const PaymentErrorPage = lazy(() => import('./pages/payment/PaymentErrorPage'));
const InventoryModule = lazy(() => import('./pages/modules/inventory/InventoryModule'));
const InventoryProductsPage = lazy(() => import('./pages/modules/inventory/ProductsPage'));
const InventoryCategoriesPage = lazy(() => import('./pages/modules/inventory/CategoriesPage'));
const WarehousesPage = lazy(() => import('./pages/modules/inventory/WarehousesPage'));
const SuppliersPage = lazy(() => import('./pages/modules/inventory/SuppliersPage'));
const StockMovementsPage = lazy(() => import('./pages/modules/inventory/StockMovementsPage'));
const WaybillsPage = lazy(() => import('./pages/modules/inventory/WaybillsPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/modules/inventory/PurchaseOrdersPage'));
const InventoryReportsPage = lazy(() => import('./pages/modules/inventory/ReportsPage'));
const StockCountPage = lazy(() => import('./pages/modules/inventory/StockCountPage'));
const LotTrackingPage = lazy(() => import('./pages/modules/inventory/LotTrackingPage'));
const LocationsPage = lazy(() => import('./pages/modules/inventory/LocationsPage'));
const AdvancedReportsPage = lazy(() => import('./pages/modules/inventory/AdvancedReportsPage'));
const ABCAnalysisPage = lazy(() => import('./pages/modules/inventory/ABCAnalysisPage'));
const StockReservationsPage = lazy(() => import('./pages/modules/inventory/StockReservationsPage'));
const ReorderSuggestionsPage = lazy(() => import('./pages/modules/inventory/ReorderSuggestionsPage'));
const StockValuationPage = lazy(() => import('./pages/modules/inventory/StockValuationPage'));
const BulkImportPage = lazy(() => import('./pages/modules/inventory/BulkImportPage'));
const MovementApprovalsPage = lazy(() => import('./pages/modules/inventory/MovementApprovalsPage'));
const BarcodeScannerPage = lazy(() => import('./pages/modules/inventory/BarcodeScannerPage'));
const CycleCountsPage = lazy(() => import('./pages/modules/inventory/CycleCountsPage'));
const PickingOrdersPage = lazy(() => import('./pages/modules/inventory/PickingOrdersPage'));
const TransferApprovalsPage = lazy(() => import('./pages/modules/inventory/TransferApprovalsPage'));
const UnitConversionsPage = lazy(() => import('./pages/modules/inventory/UnitConversionsPage'));
const AdjustmentReasonsPage = lazy(() => import('./pages/modules/inventory/AdjustmentReasonsPage'));
const StockForecastingPage = lazy(() => import('./pages/modules/inventory/StockForecastingPage'));
const ConsignmentStockPage = lazy(() => import('./pages/modules/inventory/ConsignmentStockPage'));
const RealtimeDashboardPage = lazy(() => import('./pages/modules/inventory/RealtimeDashboardPage'));
const HistoricalValuationPage = lazy(() => import('./pages/modules/inventory/HistoricalValuationPage'));
const IntercompanyTransferPage = lazy(() => import('./pages/modules/inventory/IntercompanyTransferPage'));
const SalesModule = lazy(() => import('./pages/modules/sales/SalesModule'));
// CRM Module
const CRMModule = lazy(() => import('./pages/modules/crm/CRMModule'));
const LeadsPage = lazy(() => import('./pages/modules/crm/LeadsPage'));
const OpportunitiesPage = lazy(() => import('./pages/modules/crm/OpportunitiesPage'));
const ContactsPage = lazy(() => import('./pages/modules/crm/ContactsPage'));
const ActivitiesPage = lazy(() => import('./pages/modules/crm/ActivitiesPage'));
const CRMReportsPage = lazy(() => import('./pages/modules/crm/ReportsPage'));
const CRMDashboardPage = lazy(() => import('./pages/modules/crm/DashboardPage'));
const PipelinePage = lazy(() => import('./pages/modules/crm/PipelinePage'));
const CRMTasksPage = lazy(() => import('./pages/modules/crm/TasksPage'));
const CampaignsPage = lazy(() => import('./pages/modules/crm/CampaignsPage'));
const EmailTemplatesPage = lazy(() => import('./pages/modules/crm/EmailTemplatesPage'));
// Tasks Module
const TasksModule = lazy(() => import('./pages/modules/tasks/TasksModule'));
const TasksKanbanPage = lazy(() => import('./pages/modules/tasks/TasksKanbanPage'));
const SalesDashboard = lazy(() => import('./pages/modules/sales/SalesDashboard'));
const SalesCustomersPage = lazy(() => import('./pages/modules/sales/CustomersPage'));
const CustomerDetailPage = lazy(() => import('./pages/modules/sales/CustomerDetailPage'));
const SalesProductsPage = lazy(() => import('./pages/modules/sales/ProductsPage'));
const QuotesPage = lazy(() => import('./pages/modules/sales/QuotesPage'));
const SalesDocumentsPage = lazy(() => import('./pages/modules/sales/SalesDocumentsPage'));
const OrdersPage = lazy(() => import('./pages/modules/sales/OrdersPage'));
const InvoicesPage = lazy(() => import('./pages/modules/sales/InvoicesPage'));
const SubscriptionsPage = lazy(() => import('./pages/modules/sales/SubscriptionsPage'));
const CustomerPortal = lazy(() => import('./pages/portal/CustomerPortal'));
const PanelPublicPage = lazy(() => import('./pages/portal/PanelPublicPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
// Lazy so PermissionsPage (imported by SettingsPage) builds its module-scope
// label table after the active locale has loaded, not at app start.
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ApprovalsInboxPage = lazy(() => import('./pages/ApprovalsInboxPage'));
const BillingSettingsPage = lazy(() => import('./pages/BillingSettingsPage'));
const LicenseInactivePage = lazy(() => import('./pages/LicenseInactivePage'));
const SubscriptionsDashboard = lazy(() => import('./pages/modules/sales/SubscriptionsDashboard'));
const SalesForecastPage = lazy(() => import('./pages/modules/sales/SalesForecastPage'));
const RFMAnalysisPage = lazy(() => import('./pages/modules/sales/RFMAnalysisPage'));
const MarginAnalysisPage = lazy(() => import('./pages/modules/sales/MarginAnalysisPage'));
const SalesTargetsPage = lazy(() => import('./pages/modules/sales/SalesTargetsPage'));
const TieredPricingPage = lazy(() => import('./pages/modules/sales/TieredPricingPage'));
const PromotionsPage = lazy(() => import('./pages/modules/sales/PromotionsPage'));
const SalesReportsPage = lazy(() => import('./pages/modules/sales/SalesReportsPage'));
const PurchasingModule = lazy(() => import('./pages/modules/purchasing/PurchasingModule'));
const PurchaseRequestsPage = lazy(() => import('./pages/modules/purchasing/PurchaseRequestsPage'));
const RFQsPage = lazy(() => import('./pages/modules/purchasing/RFQsPage'));
const SupplierQuotesPage = lazy(() => import('./pages/modules/purchasing/SupplierQuotesPage'));
const PurchasingOrdersPage = lazy(() => import('./pages/modules/purchasing/PurchaseOrdersPage'));
const GoodsReceiptsPage = lazy(() => import('./pages/modules/purchasing/GoodsReceiptsPage'));
const TendersModule = lazy(() => import('./pages/modules/tender/TendersModule'));
const TendersPage = lazy(() => import('./pages/modules/tender/TendersPage'));
const TenderDetailPage = lazy(() => import('./pages/modules/tender/TenderDetailPage'));
const TenderEvaluationPage = lazy(() => import('./pages/modules/tender/TenderEvaluationPage'));
const SupplierManagementPage = lazy(() => import('./pages/modules/tender/SupplierManagementPage'));
const LiveAuctionPage = lazy(() => import('./pages/modules/tender/LiveAuctionPage'));
const LiveAuctionsListPage = lazy(() => import('./pages/modules/tender/LiveAuctionsListPage'));
const BidsPage = lazy(() => import('./pages/modules/tender/BidsPage'));
const GuaranteesPage = lazy(() => import('./pages/modules/tender/GuaranteesPage'));
const TenderReportsPage = lazy(() => import('./pages/modules/tender/ReportsPage'));
const ObjectionsPage = lazy(() => import('./pages/modules/tender/ObjectionsPage'));
const DocumentsPage = lazy(() => import('./pages/modules/tender/DocumentsPage'));
const CommissionPage = lazy(() => import('./pages/modules/tender/CommissionPage'));
const SupplierDashboard = lazy(() => import('./pages/supplier/SupplierDashboard'));
const SupplierTendersPage = lazy(() => import('./pages/supplier/SupplierTendersPage'));
const SupplierBidPage = lazy(() => import('./pages/supplier/SupplierBidPage'));
const SupplierLiveAuctionPage = lazy(() => import('./pages/supplier/SupplierLiveAuctionPage'));
const SupplierMyBidsPage = lazy(() => import('./pages/supplier/SupplierMyBidsPage'));
const SupplierForgotPasswordPage = lazy(() => import('./pages/supplier/SupplierForgotPasswordPage'));
const SupplierResetPasswordPage = lazy(() => import('./pages/supplier/SupplierResetPasswordPage'));
const AccountingModule = lazy(() => import('./pages/modules/accounting/AccountingModule'));
const CashFlowReportPage = lazy(() => import('./pages/modules/accounting/cashflow/CashFlowReportPage'));
const ChartOfAccountsPage = lazy(() => import('./pages/modules/accounting/ChartOfAccountsPage'));
const AccountingInvoicesPage = lazy(() => import('./pages/modules/accounting/InvoicesPage'));
const PendingInvoicesPage = lazy(() => import('./pages/modules/accounting/PendingInvoicesPage'));
const PaymentsPage = lazy(() => import('./pages/modules/accounting/PaymentsPage'));
const BankCashPage = lazy(() => import('./pages/modules/accounting/BankCashPage'));
const ChequesNotesPage = lazy(() => import('./pages/modules/accounting/ChequesNotesPage'));
const ExpensesPage = lazy(() => import('./pages/modules/accounting/ExpensesPage'));
const ReportsPage = lazy(() => import('./pages/modules/accounting/ReportsPage'));
const CurrentAccountsPage = lazy(() => import('./pages/modules/accounting/CurrentAccountsPage'));
const AgingAnalysisPage = lazy(() => import('./pages/modules/accounting/AgingAnalysisPage'));
const ReconciliationPage = lazy(() => import('./pages/modules/accounting/ReconciliationPage'));
const IncomeStatementPage = lazy(() => import('./pages/modules/accounting/IncomeStatementPage'));
const VATReportPage = lazy(() => import('./pages/modules/accounting/VATReportPage'));
const AccountingIntegrationsPage = lazy(() => import('./pages/modules/accounting/AccountingIntegrationsPage'));
const IyzicoSettingsPage = lazy(() => import('./pages/modules/finance/IyzicoSettingsPage'));
const PayTRSettingsPage = lazy(() => import('./pages/modules/finance/PayTRSettingsPage'));
const FinanceModule = lazy(() => import('./pages/modules/finance/FinanceModule'));
const FinanceDashboard = lazy(() => import('./pages/modules/finance/FinanceDashboard'));
const AssetsModule = lazy(() => import('./pages/modules/assets/AssetsModule'));
const AssetsList = lazy(() => import('./pages/modules/assets/AssetsList'));
const CategoriesPage = lazy(() => import('./pages/modules/assets/CategoriesPage'));
const AssignmentsPage = lazy(() => import('./pages/modules/assets/AssignmentsPage'));
const AssetsMaintenancePage = lazy(() => import('./pages/modules/assets/MaintenancePage'));
const OfficeModule = lazy(() => import('./pages/modules/office/OfficeModule'));
const AnnouncementsPage = lazy(() => import('./pages/modules/office/AnnouncementsPage'));
const MeetingRoomsPage = lazy(() => import('./pages/modules/office/MeetingRoomsPage'));
const PoliciesPage = lazy(() => import('./pages/modules/office/PoliciesPage'));
const VisitorsPage = lazy(() => import('./pages/modules/office/VisitorsPage'));
const VehiclesPage = lazy(() => import('./pages/modules/office/VehiclesPage'));
const OfficeMaintenancePage = lazy(() => import('./pages/modules/office/MaintenancePage'));
const SuppliesPage = lazy(() => import('./pages/modules/office/SuppliesPage'));
const LettersOfGuaranteePage = lazy(() => import('./pages/modules/finance/LettersOfGuaranteePage'));
const LoansPage = lazy(() => import('./pages/modules/finance/LoansPage'));
const CreditCardsPage = lazy(() => import('./pages/modules/finance/CreditCardsPage'));
const FinanceTransactionsPage = lazy(() => import('./pages/modules/finance/TransactionsPage'));
const FinanceCategoriesPage = lazy(() => import('./pages/modules/finance/CategoriesPage'));
const FinancialReportsPage = lazy(() => import('./pages/modules/finance/FinancialReportsPage'));
const BudgetManagementPage = lazy(() => import('./pages/modules/finance/BudgetManagementPage'));
const CurrencyOperationsPage = lazy(() => import('./pages/modules/finance/CurrencyOperationsPage'));
const InvestmentsPage = lazy(() => import('./pages/modules/finance/InvestmentsPage'));
// Production Module
const ProductionModule = lazy(() => import('./pages/modules/production/ProductionModule'));
const ProductionOrdersPage = lazy(() => import('./pages/modules/production/ProductionOrdersPage'));
const PanelsPage = lazy(() => import('./pages/modules/production/PanelsPage'));
const WorkOrdersPage = lazy(() => import('./pages/modules/production/WorkOrdersPage'));
const ReservationsPage = lazy(() => import('./pages/modules/reservations/ReservationsPage'));
const InsurancePage = lazy(() => import('./pages/modules/insurance/InsurancePage'));
const EducationPage = lazy(() => import('./pages/modules/education/EducationPage'));
const MembershipPage = lazy(() => import('./pages/modules/membership/MembershipPage'));
const LedgerPage = lazy(() => import('./pages/modules/ledger/LedgerPage'));
const ProjectsPage = lazy(() => import('./pages/modules/projects/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/modules/projects/ProjectDetailPage'));
const BOMPage = lazy(() => import('./pages/modules/production/BOMPage'));
const MachinesPage = lazy(() => import('./pages/modules/production/MachinesPage'));
const QualityControlPage = lazy(() => import('./pages/modules/production/QualityControlPage'));
const ProductionReportsPage = lazy(() => import('./pages/modules/production/ProductionReportsPage'));
const ProductionPlanningPage = lazy(() => import('./pages/modules/production/ProductionPlanningPage'));
const MRPPage = lazy(() => import('./pages/modules/production/MRPPage'));
const ProductionCostPage = lazy(() => import('./pages/modules/production/ProductionCostPage'));
const ShiftsPage = lazy(() => import('./pages/modules/production/ShiftsPage'));
const GanttChartPage = lazy(() => import('./pages/modules/production/GanttChartPage'));
const ScrapPage = lazy(() => import('./pages/modules/production/ScrapPage'));
const OEEPage = lazy(() => import('./pages/modules/production/OEEPage'));



// Shown while a lazy route or the active locale loads: the text appears only
// once translations are ready (no suspending inside the Suspense fallback).
function RouteFallback() {
  const { t, ready } = useTranslation(undefined, { useSuspense: false });
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-surface-50 dark:bg-surface-950">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
        {ready && <p className="text-sm text-surface-500">{t('common.loading')}</p>}
      </div>
    </div>
  );
}

// Tab title for every page that doesn't set its own (SEO component), in the
// active language. index.html only carries the initial Turkish title.
// Also a self-referencing canonical for every route (an SEO component deeper
// in the tree overrides it); without it Google folds /modules-info, /signup
// etc. into the home page.
function DefaultDocumentTitle() {
  const { t, ready } = useTranslation(undefined, { useSuspense: false });
  const { pathname } = useLocation();
  const canonical = `https://www.runvya.com${pathname === '/' ? '' : pathname.replace(/\/+$/, '')}`;
  return (
    <Helmet defaultTitle={ready ? t('landing.seo.defaultTitle') : undefined}>
      <link rel="canonical" href={canonical} />
    </Helmet>
  );
}

function AppContent() {
  const toast = useToast();
  
  // Set toast context for axios interceptor
  React.useEffect(() => {
    setToastContextWithQueue(toast);
  }, [toast]);

  return (
    <Router>
      <MaintenanceAlert />
      <DeploymentNotification />
      <CookieConsent />
      <DefaultDocumentTitle />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
          {/* The mobile app opens on the app itself, not the marketing page. */}
          <Route path="/" element={isNativeApp() ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
          <Route path="/module-details/:moduleId" element={<ModuleDetailPage />} />
          <Route path="/login" element={<LoginPage />} />
          {/* store apps: no sign-up or checkout in the app (see canBuyInApp) */}
          <Route path="/signup" element={canBuyInApp() ? <SignupPage /> : <Navigate to="/login" replace />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/modules-info" element={<ModulesInfoPage />} />
          <Route path="/partner" element={<PartnerPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/portal/:token" element={<CustomerPortal />} />
          <Route path="/p/:token" element={<PanelPublicPage />} />
          <Route path="/checkout" element={canBuyInApp() ? (
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          ) : <Navigate to="/dashboard" replace />} />
          <Route path="/license-inactive" element={
            <ProtectedRoute>
              <LicenseInactivePage />
            </ProtectedRoute>
          } />
          <Route path="/modules/admin/billing" element={
            <ProtectedRoute>
              <Layout>
                <BillingSettingsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <DashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Layout>
                  <NotificationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Approval phase 3: Onaylarım, every company user (not a licensed module) */}
          <Route
            path="/approvals"
            element={
              <ProtectedRoute>
                <Layout>
                  <ApprovalsInboxPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/superuser"
            element={
              <ProtectedRoute requireSuperuser={true}>
                <Layout>
                  <SuperuserPanel />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* the user's call history + keypad (phone app, browser phone) */}
          <Route
            path="/phone"
            element={
              <ProtectedRoute>
                <Layout>
                  <PhonePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <Layout>
                  <UsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/companies"
            element={
              <ProtectedRoute requireSuperuser={true}>
                <Layout>
                  <CompaniesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/licenses"
            element={
              <ProtectedRoute requireSuperuser={true}>
                <Layout>
                  <CompanyLicensesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/system-health"
            element={
              <ProtectedRoute requireSuperuser={true}>
                <Layout>
                  <SystemHealthPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/customers"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/tickets"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceTicketsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/tickets/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceTicketFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/tickets/:ticketId/edit"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceTicketFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/tickets/:ticketId"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceTicketDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/spare-parts"
            element={
              <ProtectedRoute>
                <Layout>
                  <SparePartsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/appointments"
            element={
              <ProtectedRoute>
                <Layout>
                  <AppointmentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/categories"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProductCategoriesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/products"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProductsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/calendar"
            element={
              <ProtectedRoute>
                <Layout>
                  <TechnicianCalendarPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/history"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceHistoryPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/feedback"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerFeedbackPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/warranty-claims"
            element={
              <ProtectedRoute>
                <Layout>
                  <WarrantyClaimsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/contracts"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceContractsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/work-orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceWorkOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/service/maintenance-plans"
            element={
              <ProtectedRoute>
                <Layout>
                  <ServiceMaintenancePlansPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr"
            element={
              <ProtectedRoute>
                <Layout>
                  <HRModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/departments"
            element={
              <ProtectedRoute>
                <Layout>
                  <DepartmentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/positions"
            element={
              <ProtectedRoute>
                <Layout>
                  <PositionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/employees"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmployeesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/employees/new"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmployeeFormPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/employees/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmployeeDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/leave-types"
            element={
              <ProtectedRoute>
                <Layout>
                  <LeaveTypesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/leave-requests"
            element={
              <ProtectedRoute>
                <Layout>
                  <LeaveRequestsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/my-leaves"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyLeavesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/my-payslips"
            element={
              <ProtectedRoute>
                <Layout>
                  <MyPayslipsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/attendance"
            element={
              <ProtectedRoute>
                <Layout>
                  <AttendancePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/leave-approvals"
            element={
              <ProtectedRoute>
                <Layout>
                  <LeaveApprovalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/payroll"
            element={
              <ProtectedRoute>
                <Layout>
                  <PayrollPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/organization-chart"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrganizationChartPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/hr/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <HRDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Payment Routes */}
          <Route
            path="/payment/plans"
            element={!canBuyInApp() ? <Navigate to="/dashboard" replace /> : (
              <ProtectedRoute>
                <Layout>
                  <LicensePlansPage />
                </Layout>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/payment/checkout"
            element={!canBuyInApp() ? <Navigate to="/dashboard" replace /> : (
              <ProtectedRoute>
                <Layout>
                  <PaymentPage />
                </Layout>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/payment/success"
            element={
              <PaymentSuccessPage />
            }
          />
          <Route
            path="/payment/error"
            element={
              <PaymentErrorPage />
            }
          />
          <Route
            path="/modules/inventory"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/products"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryProductsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/categories"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryCategoriesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/warehouses"
            element={
              <ProtectedRoute>
                <Layout>
                  <WarehousesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/suppliers"
            element={
              <ProtectedRoute>
                <Layout>
                  <SuppliersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/waybills"
            element={
              <ProtectedRoute>
                <Layout>
                  <WaybillsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/movements"
            element={
              <ProtectedRoute>
                <Layout>
                  <StockMovementsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/purchase-orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <PurchaseOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <InventoryReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/stock-counts"
            element={
              <ProtectedRoute>
                <Layout>
                  <StockCountPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/lot-tracking"
            element={
              <ProtectedRoute>
                <Layout>
                  <LotTrackingPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/locations"
            element={
              <ProtectedRoute>
                <Layout>
                  <LocationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/advanced-reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdvancedReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/abc-analysis"
            element={
              <ProtectedRoute>
                <Layout>
                  <ABCAnalysisPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/reservations"
            element={
              <ProtectedRoute>
                <Layout>
                  <StockReservationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/reorder"
            element={
              <ProtectedRoute>
                <Layout>
                  <ReorderSuggestionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/valuation"
            element={
              <ProtectedRoute>
                <Layout>
                  <StockValuationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/bulk-import"
            element={
              <ProtectedRoute>
                <Layout>
                  <BulkImportPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/approvals"
            element={
              <ProtectedRoute>
                <Layout>
                  <MovementApprovalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* R5-PS-05: approval rounds stored this link before it was fixed */}
          <Route path="/modules/inventory/movement-approvals" element={<Navigate to="/modules/inventory/approvals" replace />} />
          <Route
            path="/modules/inventory/barcode"
            element={
              <ProtectedRoute>
                <Layout>
                  <BarcodeScannerPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/cycle-counts"
            element={
              <ProtectedRoute>
                <Layout>
                  <CycleCountsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/picking"
            element={
              <ProtectedRoute>
                <Layout>
                  <PickingOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/transfer-approvals"
            element={
              <ProtectedRoute>
                <Layout>
                  <TransferApprovalsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/unit-conversions"
            element={
              <ProtectedRoute>
                <Layout>
                  <UnitConversionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/adjustment-reasons"
            element={
              <ProtectedRoute>
                <Layout>
                  <AdjustmentReasonsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/forecasting"
            element={
              <ProtectedRoute>
                <Layout>
                  <StockForecastingPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/consignment"
            element={
              <ProtectedRoute>
                <Layout>
                  <ConsignmentStockPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/realtime-dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <RealtimeDashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/historical-valuation"
            element={
              <ProtectedRoute>
                <Layout>
                  <HistoricalValuationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/inventory/intercompany-transfer"
            element={
              <ProtectedRoute>
                <Layout>
                  <IntercompanyTransferPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          {/* CRM Module Routes */}
          <Route
            path="/modules/crm"
            element={
              <ProtectedRoute>
                <Layout>
                  <CRMModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/leads"
            element={
              <ProtectedRoute>
                <Layout>
                  <LeadsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/opportunities"
            element={
              <ProtectedRoute>
                <Layout>
                  <OpportunitiesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/contacts"
            element={
              <ProtectedRoute>
                <Layout>
                  <ContactsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/activities"
            element={
              <ProtectedRoute>
                <Layout>
                  <ActivitiesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <CRMReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <CRMDashboardPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/pipeline"
            element={
              <ProtectedRoute>
                <Layout>
                  <PipelinePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/tasks"
            element={
              <ProtectedRoute>
                <Layout>
                  <CRMTasksPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/crm/campaigns"
            element={
              <ProtectedRoute>
                <Layout>
                  <CampaignsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* Santral settings moved to Ayarlar (company settings); keep old links working. */}
          <Route path="/modules/crm/telephony" element={<Navigate to="/settings?tab=telephony" replace />} />
          <Route
            path="/modules/crm/email-templates"
            element={
              <ProtectedRoute>
                <Layout>
                  <EmailTemplatesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Tasks Module Routes */}
          <Route
            path="/modules/tasks"
            element={
              <ProtectedRoute>
                <Layout>
                  <TasksModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tasks/kanban"
            element={
              <ProtectedRoute>
                <Layout>
                  <TasksKanbanPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/modules/sales/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/customers"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesCustomersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/customers/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <CustomerDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/products"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesProductsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/quotes"
            element={
              <ProtectedRoute>
                <Layout>
                  <QuotesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/documents"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesDocumentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/modules/sales/orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <OrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/invoices"
            element={
              <ProtectedRoute>
                <Layout>
                  <InvoicesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/subscriptions"
            element={
              <ProtectedRoute>
                <Layout>
                  <SubscriptionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/subscriptions/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <SubscriptionsDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/forecast"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesForecastPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/rfm"
            element={
              <ProtectedRoute>
                <Layout>
                  <RFMAnalysisPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/margin-analysis"
            element={
              <ProtectedRoute>
                <Layout>
                  <MarginAnalysisPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/targets"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesTargetsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/pricing"
            element={
              <ProtectedRoute>
                <Layout>
                  <TieredPricingPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/promotions"
            element={
              <ProtectedRoute>
                <Layout>
                  <PromotionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/sales/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <SalesReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/purchasing"
            element={
              <ProtectedRoute>
                <Layout>
                  <PurchasingModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/purchasing/requests"
            element={
              <ProtectedRoute>
                <Layout>
                  <PurchaseRequestsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/purchasing/rfq"
            element={
              <ProtectedRoute>
                <Layout>
                  <RFQsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/purchasing/quotes"
            element={
              <ProtectedRoute>
                <Layout>
                  <SupplierQuotesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/purchasing/orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <PurchasingOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/purchasing/receipts"
            element={
              <ProtectedRoute>
                <Layout>
                  <GoodsReceiptsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender"
            element={
              <ProtectedRoute>
                <Layout>
                  <TendersModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/tenders"
            element={
              <ProtectedRoute>
                <Layout>
                  <TendersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/tenders/:tenderId"
            element={
              <ProtectedRoute>
                <Layout>
                  <TenderDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/evaluations/:tenderId"
            element={
              <ProtectedRoute>
                <Layout>
                  <TenderEvaluationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/suppliers"
            element={
              <ProtectedRoute>
                <Layout>
                  <SupplierManagementPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/live-auctions"
            element={
              <ProtectedRoute>
                <Layout>
                  <LiveAuctionsListPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/live-auction/:auctionId"
            element={
              <ProtectedRoute>
                <Layout>
                  <LiveAuctionPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/bids"
            element={
              <ProtectedRoute>
                <Layout>
                  <BidsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/guarantees"
            element={
              <ProtectedRoute>
                <Layout>
                  <GuaranteesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <TenderReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/objections"
            element={
              <ProtectedRoute>
                <Layout>
                  <ObjectionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/documents"
            element={
              <ProtectedRoute>
                <Layout>
                  <DocumentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/tender/commission"
            element={
              <ProtectedRoute>
                <Layout>
                  <CommissionPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Accounting Module Routes */}
          <Route
            path="/modules/accounting"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountingModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/chart-of-accounts"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChartOfAccountsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/invoices"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountingInvoicesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/pending-invoices"
            element={
              <ProtectedRoute>
                <Layout>
                  <PendingInvoicesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/payments"
            element={
              <ProtectedRoute>
                <Layout>
                  <PaymentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/bank-cash"
            element={
              <ProtectedRoute>
                <Layout>
                  <BankCashPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/cheques-notes"
            element={
              <ProtectedRoute>
                <Layout>
                  <ChequesNotesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/expenses"
            element={
              <ProtectedRoute>
                <Layout>
                  <ExpensesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <ReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* the cash flow is Finans' report; Ön Muhasebe's address opens it */}
          <Route path="/modules/accounting/cash-flow" element={<Navigate to="/modules/finance/cash-flow" replace />} />
          <Route
            path="/modules/accounting/current-accounts"
            element={
              <ProtectedRoute>
                <Layout>
                  <CurrentAccountsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/aging"
            element={
              <ProtectedRoute>
                <Layout>
                  <AgingAnalysisPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/reconciliation"
            element={
              <ProtectedRoute>
                <Layout>
                  <ReconciliationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/income-statement"
            element={
              <ProtectedRoute>
                <Layout>
                  <IncomeStatementPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/vat-report"
            element={
              <ProtectedRoute>
                <Layout>
                  <VATReportPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/accounting/integrations"
            element={
              <ProtectedRoute>
                <Layout>
                  <AccountingIntegrationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* The old Paraşüt-only page: the OAuth callback and bookmarks still land here. */}
          <Route path="/modules/accounting/parasut" element={<Navigate to="/modules/accounting/integrations" replace />} />
          <Route path="/accounting/settings/parasut" element={<Navigate to="/modules/accounting/integrations" replace />} />
          <Route
            path="/modules/finance/iyzico"
            element={
              <ProtectedRoute>
                <Layout>
                  <IyzicoSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/paytr"
            element={
              <ProtectedRoute>
                <Layout>
                  <PayTRSettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Finance Module Routes */}
          <Route
            path="/modules/finance"
            element={
              <ProtectedRoute>
                <Layout>
                  <FinanceModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/dashboard"
            element={
              <ProtectedRoute>
                <Layout>
                  <FinanceDashboard />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/letters-of-guarantee"
            element={
              <ProtectedRoute>
                <Layout>
                  <LettersOfGuaranteePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/loans"
            element={
              <ProtectedRoute>
                <Layout>
                  <LoansPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/credit-cards"
            element={
              <ProtectedRoute>
                <Layout>
                  <CreditCardsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/cash-flow"
            element={
              <ProtectedRoute>
                <Layout>
                  <CashFlowReportPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          {/* The transfer screen only simulated sending to a bank: hidden until a real bank link
              (Yapı Kredi EHÖ) replaces it; its records are kept. */}
          <Route path="/modules/finance/bank-integration" element={<Navigate to="/modules/finance" replace />} />
          <Route
            path="/modules/finance/transactions"
            element={
              <ProtectedRoute>
                <Layout>
                  <FinanceTransactionsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/modules/finance/bank-accounts" element={<Navigate to="/modules/accounting/bank-cash" replace />} />
          <Route
            path="/modules/finance/categories"
            element={
              <ProtectedRoute>
                <Layout>
                  <FinanceCategoriesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/financial-reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <FinancialReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/budget-management"
            element={
              <ProtectedRoute>
                <Layout>
                  <BudgetManagementPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/modules/finance/collection-tracking" element={<Navigate to="/modules/accounting/aging" replace />} />
          <Route
            path="/modules/finance/currency-operations"
            element={
              <ProtectedRoute>
                <Layout>
                  <CurrencyOperationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/finance/investments"
            element={
              <ProtectedRoute>
                <Layout>
                  <InvestmentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Assets Module Routes */}
          <Route
            path="/modules/assets"
            element={
              <ProtectedRoute>
                <Layout>
                  <AssetsModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/assets/list"
            element={
              <ProtectedRoute>
                <Layout>
                  <AssetsList />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/assets/categories"
            element={
              <ProtectedRoute>
                <Layout>
                  <CategoriesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/assets/assignments"
            element={
              <ProtectedRoute>
                <Layout>
                  <AssignmentsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/assets/maintenance"
            element={
              <ProtectedRoute>
                <Layout>
                  <AssetsMaintenancePage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Office Module Routes */}
          <Route
            path="/modules/office"
            element={
              <ProtectedRoute>
                <Layout>
                  <OfficeModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/office/announcements"
            element={
              <ProtectedRoute>
                <Layout>
                  <AnnouncementsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/office/meeting-rooms"
            element={
              <ProtectedRoute>
                <Layout>
                  <MeetingRoomsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/office/policies"
            element={
              <ProtectedRoute>
                <Layout>
                  <PoliciesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/office/visitors"
            element={
              <ProtectedRoute>
                <Layout>
                  <VisitorsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/office/vehicles"
            element={
              <ProtectedRoute>
                <Layout>
                  <VehiclesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/office/maintenance"
            element={
              <ProtectedRoute>
                <Layout>
                  <OfficeMaintenancePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/office/supplies"
            element={
              <ProtectedRoute>
                <Layout>
                  <SuppliesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Production Module Routes */}
          <Route
            path="/modules/production"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProductionModule />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/reservations"
            element={
              <ProtectedRoute>
                <Layout>
                  <ReservationsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/insurance"
            element={
              <ProtectedRoute>
                <Layout>
                  <InsurancePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/education"
            element={
              <ProtectedRoute>
                <Layout>
                  <EducationPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/membership"
            element={
              <ProtectedRoute>
                <Layout>
                  <MembershipPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/projects"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProjectsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/projects/:id"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProjectDetailPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/ledger"
            element={
              <ProtectedRoute>
                <Layout>
                  <LedgerPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/ledger/accounts"
            element={
              <ProtectedRoute>
                <Layout>
                  <LedgerPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/ledger/journal"
            element={
              <ProtectedRoute>
                <Layout>
                  <LedgerPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/ledger/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <LedgerPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProductionOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/panels"
            element={
              <ProtectedRoute>
                <Layout>
                  <PanelsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/work-orders"
            element={
              <ProtectedRoute>
                <Layout>
                  <WorkOrdersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/bom"
            element={
              <ProtectedRoute>
                <Layout>
                  <BOMPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/machines"
            element={
              <ProtectedRoute>
                <Layout>
                  <MachinesPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/quality"
            element={
              <ProtectedRoute>
                <Layout>
                  <QualityControlPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/reports"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProductionReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/planning"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProductionPlanningPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/mrp"
            element={
              <ProtectedRoute>
                <Layout>
                  <MRPPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/costs"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProductionCostPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/shifts"
            element={
              <ProtectedRoute>
                <Layout>
                  <ShiftsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/gantt"
            element={
              <ProtectedRoute>
                <Layout>
                  <GanttChartPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/scrap"
            element={
              <ProtectedRoute>
                <Layout>
                  <ScrapPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules/production/oee"
            element={
              <ProtectedRoute>
                <Layout>
                  <OEEPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          {/* Supplier Portal Routes */}
          <Route path="/supplier/login" element={<SupplierLoginPage />} />
          <Route path="/supplier/forgot-password" element={<SupplierForgotPasswordPage />} />
          <Route path="/supplier/reset-password" element={<SupplierResetPasswordPage />} />
          <Route path="/supplier/dashboard" element={<SupplierDashboard />} />
          <Route path="/supplier/tenders" element={<SupplierTendersPage />} />
          <Route path="/supplier/tender/:tenderId" element={<SupplierTendersPage />} />
          <Route path="/supplier/tender/:tenderId/bid" element={<SupplierBidPage />} />
          <Route path="/supplier/auction/:auctionId" element={<SupplierLiveAuctionPage />} />
          <Route path="/supplier/my-bids" element={<SupplierMyBidsPage />} />

          {/* Unknown routes: translated 404 instead of a blank page */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </Router>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <ToastProvider>
          <ConfirmDialogProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </ConfirmDialogProvider>
        </ToastProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;